import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
const SliderComponent = !!Slider.default ? Slider.default : Slider; //hotfix: https://github.com/akiran/react-slick/issues/2206#issuecomment-1961173249
import { ArrowDirection, GalleryProps } from './types';
import {
  Container,
  GalleryButton,
  SliderDot,
  SliderDotContainer,
  SliderDots,
  SliderItem,
  SliderThumbnail,
  SliderThumbnailItem,
  SliderWrapper,
} from './styles';
import { useDrag } from 'src/hooks/useDrag';
import { GalleryItem } from './GalleryItem';
import { GalleryThumbnail } from './GalleryThumbnail';
import Icon from 'src/units/Icon';
import { Arrow } from './Arrow';
import { DeviceManager, isVimeoUrl, isYoutubeUrl } from 'src/utils';
import { isIOS } from 'react-device-detect';

const defaultSettings = {
  thumbnails: true,
  fullScreen: true,
  controlArrows: false,
  dots: true,
  imageHeight: 200,
  thumbnailSize: 52,
  thumbnailBorderWidth: 1,
  thumbnailsGap: 10,
  thumbnailsMarginRight: 0,
};

export const Gallery: FC<GalleryProps & { children?: any }> = ({
  items,
  onGalleryItemClick,
  settings = defaultSettings,
}) => {
  const gallerySettings = { ...defaultSettings, ...settings };
  const [slideIndex, setSlideIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const isDragging = useDrag();
  const [galleryWidth, setGalleryWidth] = useState(0);
  const [inFullScreen, setInFullScreen] = useState(false);

  const containerRef = useRef<any>(null);
  const sliderRef = useRef<any>(null);
  const sliderThumbnailRef = useRef(null);

  const thumbnailsLength =
    items.length *
      (gallerySettings.thumbnailSize +
        gallerySettings.thumbnailsGap +
        gallerySettings.thumbnailBorderWidth * 2) -
    10;
  const thumbnailsRowWidth =
    galleryWidth && thumbnailsLength < galleryWidth ? `${thumbnailsLength}px` : 'auto';

  const pauseAllVideos = () => {
    if (!sliderRef.current) return;
    sliderRef.current?.innerSlider.list.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  };

  // Fix iframe duplication
  useEffect(() => {
    if (!sliderRef.current) return;
    const iframes: HTMLIFrameElement[] =
      sliderRef.current?.innerSlider.list.querySelectorAll('iframe');
    const seenSrcs = new Set();

    iframes.forEach((iframe) => {
      const src = iframe.getAttribute('src');
      if (src && (isVimeoUrl(src) || isYoutubeUrl(src))) {
        if (seenSrcs.has(src)) {
          iframe.setAttribute('src', '');
        } else {
          seenSrcs.add(src);
        }
      }
    });
  });

  const setSlide = (index: number) => {
    if (isChanging || index === slideIndex || index < 0 || index >= items.length) return;

    pauseAllVideos();
    setSlideIndex(index);

    if (sliderRef.current && sliderRef.current.slickGoTo) {
      try {
        // @ts-ignore
        sliderRef.current.slickGoTo(index);
      } catch (error) {
        console.warn('Error navigating main slider:', error);
      }
    }

    if (sliderThumbnailRef.current && sliderThumbnailRef.current.slickGoTo) {
      try {
        // @ts-ignore
        sliderThumbnailRef.current.slickGoTo(index);
      } catch (error) {
        console.warn('Error navigating thumbnail slider:', error);
      }
    }
  };

  const navigateToNextSlide = () => {
    const nextIndex = slideIndex === items.length - 1 ? 0 : slideIndex + 1;
    setSlide(nextIndex);
  };

  const navigateToPrevSlide = () => {
    const prevIndex = slideIndex === 0 ? items.length - 1 : slideIndex - 1;
    setSlide(prevIndex);
  };

  const toggleFullscreen = (ref) => {
    if (!document.fullscreenElement) {
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else {
      if (document.exitFullscreen) {
        setInFullScreen(false);
        document.exitFullscreen();
      }
    }
  };
  useLayoutEffect(() => {
    // @ts-ignore
    const { width } = containerRef.current.getBoundingClientRect();
    setGalleryWidth(width);
  }, [inFullScreen]);

  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  // Add keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (inFullScreen) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            navigateToPrevSlide();
            break;
          case 'ArrowRight':
            event.preventDefault();
            navigateToNextSlide();
            break;
          case 'Escape':
            event.preventDefault();
            if (document.exitFullscreen) {
              setInFullScreen(false);
              document.exitFullscreen();
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inFullScreen, slideIndex, items.length]);

  const [galleryItemHeight, setGalleryItemHeight] = useState(0);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const aspectRatio = 1920 / 1080;
      const calculatedHeight = containerWidth / aspectRatio;
      setGalleryItemHeight(calculatedHeight);
    }
  }, [inFullScreen]);

  return (
    <Container ref={containerRef}>
      {galleryWidth && (
        <>
          {!isIOS && gallerySettings.fullScreen && !inFullScreen && (
            <GalleryButton
              style={{
                position: 'absolute',
                top: DeviceManager.isMobile()
                  ? `${galleryItemHeight - 10 - 36}px`
                  : `${galleryItemHeight - 20 - 48}px`,
                right: DeviceManager.isMobile() ? '10px' : '20px',
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
                linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))`,
                // @ts-ignore
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                },
                // @ts-ignore
                ' &:focus': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                },
              }}
              onClick={() => toggleFullscreen(containerRef)}
            >
              <Icon.FullScreen />
            </GalleryButton>
          )}
          {!isIOS && inFullScreen && (
            <GalleryButton
              style={{
                position: 'fixed',
                top: `20px`,
                right: '20px',
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)),
                linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))`,
                // @ts-ignore
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                },
                // @ts-ignore
                ' &:focus': {
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                },
              }}
              onClick={() => toggleFullscreen(containerRef)}
            >
              <Icon.Close
                style={{ color: 'white', width: '100%', height: '100%' }}
                fontSize="large"
              />
            </GalleryButton>
          )}
          <SliderWrapper>
            <SliderComponent
              ref={sliderRef}
              arrows={settings.controlArrows}
              nextArrow={<Arrow direction={ArrowDirection.Right} />}
              prevArrow={<Arrow direction={ArrowDirection.Left} />}
              dots={false}
              infinite={items.length > 1}
              adaptiveHeight={true}
              beforeChange={() => {
                setIsChanging(true);
              }}
              afterChange={(i) => {
                if (slideIndex !== i) setSlideIndex(i);
                if (sliderThumbnailRef.current) {
                  if (thumbnailsRowWidth === 'auto') {
                    // @ts-ignore
                    sliderThumbnailRef.current.slickGoTo(i);
                  } else {
                    // For fixed width thumbnails, ensure the active thumbnail is visible
                    const thumbnailSlider = sliderThumbnailRef.current;
                    const totalSlides = items.length;
                    if (totalSlides > 1) {
                      // @ts-ignore
                      thumbnailSlider.slickGoTo(i);
                    }
                  }
                }
                setIsChanging(false);
              }}
            >
              {items.map((item, index) => (
                <SliderItem
                  key={index}
                  maxHeight={inFullScreen ? '75vh' : `${galleryItemHeight}px`}
                  height={inFullScreen ? '75vh' : `max-content`}
                  padding={inFullScreen ? `10vh 10vw 0 10vw` : 0}
                >
                  <GalleryItem
                    item={item}
                    index={index}
                    currentIndex={slideIndex}
                    onPointerUp={() => {
                      if (!isDragging && onGalleryItemClick) onGalleryItemClick();
                    }}
                  />
                </SliderItem>
              ))}
            </SliderComponent>
          </SliderWrapper>
          {gallerySettings.thumbnails ? (
            <SliderThumbnail
              margin={
                thumbnailsRowWidth === 'auto'
                  ? `0 ${gallerySettings.thumbnailsMarginRight}px 0 0`
                  : 'auto'
              }
              width={thumbnailsRowWidth}
            >
              <SliderComponent
                ref={sliderThumbnailRef}
                arrows={false}
                dots={false}
                draggable={thumbnailsRowWidth === 'auto'}
                swipeToSlide
                variableWidth
                infinite={items.length > 1}
                beforeChange={() => {
                  setIsChanging(true);
                }}
                afterChange={() => {
                  setIsChanging(false);
                }}
              >
                {items.map((item, index) => (
                  <SliderThumbnailItem
                    key={index}
                    thumbnailBorderWidth={gallerySettings.thumbnailBorderWidth}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSlide(index);
                    }}
                  >
                    <GalleryThumbnail
                      item={item}
                      active={index === slideIndex}
                      thumbnailsGap={gallerySettings.thumbnailsGap}
                      onPointerUp={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSlide(index);
                      }}
                    />
                  </SliderThumbnailItem>
                ))}
              </SliderComponent>
            </SliderThumbnail>
          ) : null}
          {gallerySettings.dots && (
            <SliderDots style={{ marginBottom: inFullScreen ? '20px' : 0 }}>
              {items.map((_item, index) => (
                <SliderDotContainer
                  key={index}
                  onPointerUp={() => {
                    setSlide(index);
                  }}
                >
                  <SliderDot active={index === slideIndex} />
                </SliderDotContainer>
              ))}
            </SliderDots>
          )}
        </>
      )}
    </Container>
  );
};
